import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { isSameDay } from "date-fns";
import { useFullscreen } from "@mantine/hooks";
import { QueryObserverBaseResult } from "@tanstack/react-query";
import { ColumnFilter } from "Components/AppGrid/shipeezi-new-grid";
import { DespatchCancelTaskModalHandles } from "../components/Diary/popups/CancelTask";
import { DespatchChangeTimeModalHandles } from "../components/Diary/popups/ChangeTaskTime/ChangeTimeModal";
import { CreateCrewModalHandles } from "../components/Diary/popups/CreateCrew";
import { DespatchCreateTaskModalHandles } from "../components/Tasks/popups/CreateTask";
import { DespatchCreatePlaceholderTaskModalHandles } from "../components/Tasks/popups/CreateTask/DespatchCreatePlaceholderTask";
import { DespatchDeletePlaceholderTaskModalHandles } from "../components/Diary/popups/DeletePlaceholderTask";
import { DespatchTaskCommentsModalHandles } from "../components/Diary/popups/TaskComments";
import { DiarySeeTasksHandles } from "../components/Diary/SeeTasks";
import { DiaryType } from "Components/DiaryCalendar";
import { StorageBoxSingleUseModalHandles } from "Components/Generic/StorageBoxSingleUseModal";
import { SELECTED_COMPANIES_IDS, SELECTED_TASK_TYPES } from "Constants";
import { useAppState } from "Context/StateContext";
import { DespatchCrew } from "Models/despatch.crew";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { differenceInDays, startOfDay } from "date-fns";
import { isTaskMultiDay } from "Util/isTaskMultiday";
import { isTheSameDay } from "Util/startDate";

import {
  DespatchTask,
  DespatchTaskComment,
  TaskStorageBox,
  TeamFleet,
  TeamMember,
} from "Models/despatch.task";
import { Fleet } from "Models/fleet";
import { Member } from "Models/members/member";
import { Filters } from "Models/shipeezi.table";
import { StorageBox } from "Models/storage.box";
import { Task, TaskStartDatePeriod } from "Models/task";
import { Team } from "Models/team";
import { DespatchCrewPersist } from "Models/despatch.crew";
import { UseState } from "Types";
import { checkIsEmptyTask, checkIsEmptyTeam } from "Util/checkEmpty";
import { matchString } from "Util/despatchSearchFieldMatcher";
import { ResourceViewerOptions, ViewPerspective } from "Util/enums";
import { getOrderCustomerDisplayName } from "Util/names";
import { mountSearchByOrToBackend, mountSearchToBackend } from "Util/onSearch";

import {
  DespatchGlobalFilters,
  DispatchGlobalFilters,
  getDefaultDespatchFilterValues,
  PUBLISHED_TASKS,
  UNPUBLISHED_TASKS,
} from "../components/Diary/shared/DespatchFilters/types";
import { useDespatchActions } from "../components/Diary/views/DiaryResourcesView/hooks/useDespatchActions";
import { AnalyzerSlotSourceIdReturnType } from "../components/Diary/views/DiaryResourcesView/subcomponent/Viewers/useDragSystem";
import { TimeControlAndExpensePropsHandles } from "Components/TimeControlModal";
import { Branch } from "Models/branch";
import TaskDateUtils from "Util/dateUtils";

const ROWS_PER_PAGE = 10;

type UseFullscreen = ReturnType<typeof useFullscreen>;

const injectArrayDataIntoRecord = <T extends Record<number, V>, V extends any[]>(
  record: T,
  key: number,
  value: V
): T => {
  if (Array.isArray(value)) {
    (record[key] as V[]) = [...((record[key] as V[]) || []), ...value];
  }

  return record;
};

interface TaskReference {
  id: number;
  type: string;
  jobReference: string;
}

interface OperationDespatchContextProps {
  fullscreenContainerRef: UseFullscreen["ref"];
  isFullscreen: UseFullscreen["fullscreen"];
  toogleFullscreen: UseFullscreen["toggle"];
  isSearchMode: boolean;
  setIsSearchMode: UseState<boolean>;

  cancelTaskRef: React.RefObject<DespatchCancelTaskModalHandles>;
  deletePlaceholderTaskRef: React.RefObject<DespatchDeletePlaceholderTaskModalHandles>;
  changeTimeRef: React.RefObject<DespatchChangeTimeModalHandles>;
  createTaskRef: React.RefObject<DespatchCreateTaskModalHandles>;
  createPlaceholderTaskRef: React.RefObject<DespatchCreatePlaceholderTaskModalHandles>;
  storageBoxSingleUseRef: React.RefObject<StorageBoxSingleUseModalHandles>;
  taskCommentRef: React.RefObject<DespatchTaskCommentsModalHandles>;
  despatchTimeControlRef: React.RefObject<TimeControlAndExpensePropsHandles>;
  createCrewModalRef: React.RefObject<CreateCrewModalHandles>;
  seeTasksModalRef: React.RefObject<DiarySeeTasksHandles>;
  viewMode: DiaryType;
  setViewMode: UseState<DiaryType>;

  selectedOption: ResourceViewerOptions;
  setSelectedOption: (value: ResourceViewerOptions) => void;
  draggedItemHeight: number;
  setDraggedItemHeight: (value: number) => void;

  searchTerm: string;
  setSearchTerm: (value: string) => void;
  searchValue: (event: React.ChangeEvent<HTMLInputElement>) => void;

  members: Member[];
  isLoadingMembers: boolean;
  membersCount: number;
  refetchMembers: () => void;
  fetchNextMemberPage: () => void;
  hasNextMemberPage: boolean;
  isLoadingNextMemberPage: boolean;

  equipments: Fleet[];
  isLoadingEquipments: boolean;
  equipmentsCount: number;
  fetchNextEquipmentPage: () => void;
  hasNextEquipmentPage: boolean;
  isLoadingNextEquipmentPage: boolean;

  teams: Team[];
  isLoadingTeams: boolean;
  teamsCount: number;

  crews: DespatchCrew[];
  crewsFiltered: DespatchCrew[];
  isLoadingCrews: boolean;
  isFetchingCrews: boolean;
  isFetchedCrews: boolean;
  isRefetchingCrews: boolean;
  setCrews: (value: DespatchCrew[]) => void;

  storageBoxes: StorageBox[];
  isLoadingStorageBoxes: boolean;
  storageBoxesCount: number;
  fetchNextStorageBoxPage: () => void;
  hasNextStorageBoxPage: boolean;
  isLoadingNextStorageBoxPage: boolean;

  refetchTasks: QueryObserverBaseResult<DespatchTask[]>["refetch"];
  isFetchingTasks: boolean;
  isFetchedTasks: boolean;
  isRefetchingTasks: boolean;
  tasks: DespatchTask[];
  taskStatusCounter: { allTasks: number; unpublishedTasks: number; publishedTasks: number };
  tasksFiltered: DespatchTask[];
  tasksAvailable: DespatchTask[];
  taskSearch: string;
  taskSetSearch: React.Dispatch<React.SetStateAction<string>>;
  deletePlaceholderTask: (taskId: DespatchTask["id"]) => void;

  removeCrewFromBoard: (param: DespatchCrew) => void;
  addTeamToBoard: (param: Team) => void;

  updateCrewById: (
    crewIndex: number,
    updatedCrew: DespatchCrew,
    overrideProperties?: boolean,
    oldTeamToUpdate?: DespatchCrew,
    whatHasChanged?: string[]
  ) => void;
  updateTaskById: (id: number, updatedTask: DespatchTask, whatHasChanged?: string[]) => void;
  moveTaskBetweenCrews: (
    sourceCrewId: DespatchCrew["id"],
    destinationCrewId: DespatchCrew["id"],
    taskId: DespatchTask["id"],
    task: DespatchTask
  ) => void;
  isLoadingPersisTaskChanges: boolean;

  usersInTasks: Record<number, TaskReference[]>;
  usersInCrewWithTasks: Record<number, TaskReference[]>;
  equipmentsInCrewWithTasks: Record<number, TaskReference[]>;
  equipmentsInTasks: Record<number, TaskReference[]>;

  usersInCrewWithCrewName: Record<number, string[]>;
  equipmentsInCrewWithCrewName: Record<number, string[]>;

  currentDate: Date;
  setCurrentDate: React.Dispatch<React.SetStateAction<Date>>;
  currentDraggable: AnalyzerSlotSourceIdReturnType | null;
  setCurrentDraggable: (value: AnalyzerSlotSourceIdReturnType | null) => void;
  parentOfSource: AnalyzerSlotSourceIdReturnType | null;
  setParentOfSource: (value: AnalyzerSlotSourceIdReturnType | null) => void;

  updateCrewTaskValueByTaskId: (
    taskId: DespatchTask["id"],
    property: keyof DespatchTask,
    value: any
  ) => void;

  companyId: number[];
  branchId?: number[];

  addNewCrew: (crew: DespatchCrew) => void;
  addNewStorageBoxToCrew: (crew: DespatchCrew, storageBox: StorageBox) => void;
  addTaskCreatedToBoard: (task: Task, crewId?: DespatchCrew["id"] | null) => void;

  addTaskComment: (taskId: DespatchTask["id"], comment: DespatchTaskComment) => void;
  deleteTaskComment: (taskId: DespatchTask["id"], commentId: DespatchTaskComment["id"]) => void;

  expandedOrdersTasks: number[];
  collapseAllOrdersTasks: () => void;
  expandAllOrdersTasks: () => void;
  toggleOrderTaskExpanded: (taskId: number) => void;

  expandedTeams: number[];
  collapseAllTeams: () => void;
  expandAllTeams: () => void;
  toggleTeamExpanded: (crewId: number) => void;

  showUnavailableItems: boolean;
  setShowUnavailableItems: (value: boolean) => void;
  showEmptyOrdersTasks: boolean;
  setShowEmptyOrdersTasks: (value: boolean) => void;
  showEmptyTeams: boolean;
  setShowEmptyTeams: (value: boolean) => void;

  despatchSearchColumns: ColumnFilter[];
  setDespatchSearchColumns: UseState<ColumnFilter[]>;

  despatchFilters: Filters | undefined;
  setDespatchFilters: UseState<Filters | undefined>;

  despatchGlobalFilters: DespatchGlobalFilters;
  setDespatchGlobalFilters: (filter: DespatchGlobalFilters) => void;

  activeFilters: {
    memberFilters: Filters;
    teamsFilters: Filters;
    equipmentsFilters: Filters;
    storageBoxFilters: Filters;
  };
  setActiveFilters: React.Dispatch<
    React.SetStateAction<{
      memberFilters: Filters;
      teamsFilters: Filters;
      equipmentsFilters: Filters;
      storageBoxFilters: Filters;
    }>
  >;

  viewPerspective?: ViewPerspective;
  setViewPerspective: React.Dispatch<React.SetStateAction<ViewPerspective | undefined>>;
  activeSearches: {
    equipmentSearch: string;
    memberSearch: string;
    storageBoxSearch: string;
    teamSearch: string;
  };
  setActiveSearches: React.Dispatch<
    React.SetStateAction<{
      equipmentSearch: string;
      memberSearch: string;
      storageBoxSearch: string;
      teamSearch: string;
    }>
  >;

  showLeftSideView: boolean;
  showRightSideView: boolean;
  setShowLeftSideView: React.Dispatch<React.SetStateAction<boolean>>;
  setShowRightSideView: React.Dispatch<React.SetStateAction<boolean>>;
  totalItemsPerColumn: {
    ultra: number;
    xxl: number;
    xl: number;
    lg: number;
    sm: number;
  };

  changesMade: Array<DespatchCrewPersist> | null;
  setChangesMade: React.Dispatch<React.SetStateAction<Array<DespatchCrewPersist> | null>>;

  reverseResources: () => void;
  persistCrews: (callback?: () => void) => void;
  isLoadingPersistCrewsChanges?: boolean;
}

const OperationDespatchContext = createContext<OperationDespatchContextProps>(
  {} as OperationDespatchContextProps
);

const OperationDespatchProvider: React.FC = ({ children }) => {
  const { currentUser, userTimezone, formatWithUserTimezone } = useAppState();

  const [selectedOption, setSelectedOption] = useState<ResourceViewerOptions>(() => {
    const savedOption = localStorage.getItem("selectedOption");

    return savedOption ? JSON.parse(savedOption) : ResourceViewerOptions.TEAM;
  });
  const [viewMode, setViewMode] = useState<DiaryType>(DiaryType.RESOURCES);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [draggedItemHeight, setDraggedItemHeight] = useState<number>(0);

  const [changesMade, setChangesMade] = useState<Array<DespatchCrewPersist> | null>(null);
  const [oldResources, setOldResources] = useState<{
    crews: Array<DespatchCrew>;
    tasks: Array<DespatchTask>;
  } | null>(null);

  const searchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
  };
  const [taskSearch, taskSetSearch] = useState("");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [isSearchMode, setIsSearchMode] = useState(false);
  const {
    ref: fullscreenContainerRef,
    toggle: toogleFullscreen,
    fullscreen: isFullscreen,
  } = useFullscreen();

  const checkTaskHasAllowedStatus = (task: DespatchTask) => {
    if (!despatchGlobalFilters.tasksStatuses || !despatchGlobalFilters.tasksStatuses.length)
      return true;

    return despatchGlobalFilters.tasksStatuses?.includes(task.status);
  };

  const checkCrewHasAllowedTaskStatus = (crew: DespatchCrew) => {
    if (!despatchGlobalFilters.tasksStatuses || !despatchGlobalFilters.tasksStatuses.length)
      return true;

    return crew.tasks.some((task) => despatchGlobalFilters.tasksStatuses?.includes(task.status));
  };

  const {
    getTasks,
    getCrews,
    getAllMembersCount,
    getAllEquipmentsCount,
    getTeamsCount,
    getInfiniteTeams,
    usePersistCrewChanges,
    usePersistTaskChanges,
    getStorageBoxesCount,
    getInfiniteMembers,
    getInfiniteEquipments,
    getInfiniteStorageBoxes,
    useHideCrew,
    usePersistCrewsChanges,
  } = useDespatchActions();

  const { mutate: persistCrewsChanges, isLoading: isLoadingPersistCrewsChanges } =
    usePersistCrewsChanges();

  const { mutate: persistCrewChanges } = usePersistCrewChanges();
  const { mutate: persistTaskChanges, isLoading: isLoadingPersisTaskChanges } =
    usePersistTaskChanges();

  const savedSelectedIds = localStorage.getItem(SELECTED_COMPANIES_IDS);
  const savedTaskTypes = localStorage.getItem(SELECTED_TASK_TYPES);

  const [despatchGlobalFilters, setDespatchGlobalFilters] = useState<DespatchGlobalFilters>(
    getDefaultDespatchFilterValues(
      currentUser?.companyId!,
      savedSelectedIds ? JSON.parse(savedSelectedIds) : [currentUser?.companyId!],
      savedTaskTypes ? JSON.parse(savedTaskTypes) : []
    )
  );

  const companyId = useMemo(
    () =>
      despatchGlobalFilters.companyIds.length
        ? despatchGlobalFilters.companyIds
        : [currentUser?.companyId!],
    [despatchGlobalFilters, currentUser]
  );

  const [despatchFilters, setDespatchFilters] = useState<Filters>();
  const [despatchSearchColumns, setDespatchSearchColumns] = useState<ColumnFilter[]>([]);

  const [crews, setCrews] = useState<DespatchCrew[]>([]);
  const [tasks, setTasks] = useState<DespatchTask[]>([]);
  const [currentDraggable, setCurrentDraggable] = useState<AnalyzerSlotSourceIdReturnType | null>(
    null
  );
  const [showLeftSideView, setShowLeftSideView] = useState(true);
  const [showRightSideView, setShowRightSideView] = useState(true);
  const [showUnavailableItems, setShowUnavailableItems] = useState(true);
  const [showEmptyOrdersTasks, setShowEmptyOrdersTasks] = useState(true);
  const [showEmptyTeams, setShowEmptyTeams] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState<number[]>([]);
  const [expandedOrdersTasks, setExpandedOrdersTasks] = useState<number[]>([]);
  const [activeFilters, setActiveFilters] = useState({
    equipmentsFilters: {} as Filters,
    memberFilters: {} as Filters,
    storageBoxFilters: {} as Filters,
    teamsFilters: {} as Filters,
  });
  const [viewPerspective, setViewPerspective] = useState<ViewPerspective>();
  const [activeSearches, setActiveSearches] = useState({
    equipmentSearch: "",
    memberSearch: "",
    storageBoxSearch: "",
    teamSearch: "",
  });

  const [parentOfSource, setParentOfSource] = useState<AnalyzerSlotSourceIdReturnType | null>(null);

  const sideViewOpenCount = useMemo(() => {
    const sideViews = [showLeftSideView, showRightSideView];
    return sideViews.filter((sideView) => !!sideView).length;
  }, [showLeftSideView, showRightSideView]);

  const totalItemsPerColumn: OperationDespatchContextProps["totalItemsPerColumn"] = useMemo(() => {
    const getTotalSubtractionCount = (baseCount: number) => {
      return baseCount - sideViewOpenCount;
    };
    return {
      wideUltra: getTotalSubtractionCount(10),
      ultra: getTotalSubtractionCount(9),
      quad: getTotalSubtractionCount(6),
      xxl: getTotalSubtractionCount(5),
      xl: getTotalSubtractionCount(4),
      lg: getTotalSubtractionCount(4),
      md: getTotalSubtractionCount(3),
      sm: 2,
      xs: 1,
    };
  }, [sideViewOpenCount]);

  const { data: teamsCount = 0 } = getTeamsCount(
    companyId,
    currentDate,
    mountSearchToBackend(["name"], activeSearches.teamSearch),
    activeFilters.teamsFilters,
    viewPerspective
  );

  const { data: teamsData, isFetching: isLoadingTeams } = getInfiniteTeams(
    companyId,
    teamsCount,
    currentDate,
    ROWS_PER_PAGE,
    mountSearchToBackend(["name"], activeSearches.teamSearch),
    activeFilters.teamsFilters,
    viewPerspective
  );

  const teams = useMemo(() => {
    if (teamsData && teamsData.pages) {
      const actualPages = [...teamsData.pages];

      const dataPaginated = actualPages.map((page) => (page as Team[])?.map((data) => data)).flat();

      return dataPaginated;
    }

    return [];
  }, [teamsData]);

  const branchId = useMemo(() => {
    return despatchGlobalFilters?.branchIds.length
      ? despatchGlobalFilters?.branchIds
      : currentUser?.branches
          ?.filter((branch) => companyId.includes(branch.companyId!))
          ?.map((branch) => branch.id!);
  }, [currentUser?.branches, companyId, despatchGlobalFilters?.branchIds]);

  const {
    data: tasksFromAPI,
    isFetching: isFetchingTasks,
    isFetched: isFetchedTasks,
    isRefetching: isRefetchingTasks,
    refetch: refetchTasks,
  } = getTasks({
    dateSelected: currentDate,
    page: 0,
    pageSize: 100,
    companyId,
    branchId,
    viewMode,
  });

  const {
    data: crewsFromAPI,
    isLoading: isLoadingCrews,
    isFetching: isFetchingCrews,
    isFetched: isFetchedCrews,
    isRefetching: isRefetchingCrews,
  } = getCrews({
    dateSelected: currentDate,
    page: 0,
    pageSize: 100,
    companyId,
    branchId,
  });

  const { refetch: refetchMembersCount, data: membersCount = 0 } = getAllMembersCount({
    companyId,
    dateSelected: currentDate,
    branchId,
    externalFilters: activeFilters.memberFilters,
    searches: mountSearchToBackend(["full_name"], activeSearches.memberSearch),
    perspective: viewPerspective,
  });

  const {
    fetchNextPage: fetchNextMemberPage,
    hasNextPage: hasNextMemberPage,
    isLoading: isLoadingMembers,
    isFetchingNextPage: isLoadingNextMemberPage,
    data: membersData,
    refetch: refetchMembers,
  } = getInfiniteMembers({
    companyId,
    memberTotal: membersCount,
    dateSelected: currentDate,
    branchId,
    pageSize: ROWS_PER_PAGE,
    searches: mountSearchToBackend(["full_name"], activeSearches.memberSearch),
    externalFilters: activeFilters.memberFilters,
    perspective: viewPerspective,
  });

  const members = useMemo(() => {
    if (membersData && membersData.pages) {
      const actualPages = [...membersData.pages];

      const dataPaginated = actualPages
        .map((page) => (page as Member[])?.map((data) => data))
        .flat();

      return dataPaginated;
    }

    return [];
  }, [membersData]);

  const equipmentSearches = useMemo(
    () => mountSearchByOrToBackend(["fleet_number", "description"], activeSearches.equipmentSearch),
    [activeSearches.equipmentSearch]
  );

  const { refetch: refetchEquipmentCount, data: equipmentsCount = 0 } = getAllEquipmentsCount({
    companyId,
    dateSelected: currentDate,
    branchId,
    searches: equipmentSearches,
    filters: activeFilters.equipmentsFilters,
    perspective: viewPerspective,
  });

  const {
    fetchNextPage: fetchNextEquipmentPage,
    hasNextPage: hasNextEquipmentPage,
    isLoading: isLoadingEquipments,
    isFetchingNextPage: isLoadingNextEquipmentPage,
    data: equipmentsData,
    refetch: refetchEquipmentData,
  } = getInfiniteEquipments({
    companyId,
    equipmentTotal: equipmentsCount,
    dateSelected: currentDate,
    branchId,
    pageSize: ROWS_PER_PAGE,
    searches: equipmentSearches,
    filter: activeFilters.equipmentsFilters,
    perspective: viewPerspective,
  });

  const equipments = useMemo(() => {
    if (equipmentsData && equipmentsData.pages) {
      const actualPages = [...equipmentsData.pages];

      const dataPaginated = actualPages
        .map((page) => (page as Fleet[])?.map((data) => data))
        .flat();

      return dataPaginated;
    }

    return [];
  }, [equipmentsData]);

  const { refetch: refetchStorageBoxCount, data: storageBoxesCount = 0 } = getStorageBoxesCount({
    companyId,
    dateSelected: currentDate,
    branchId,
    searches: mountSearchToBackend(["number"], activeSearches.storageBoxSearch),
    filters: activeFilters.storageBoxFilters,
    perspective: viewPerspective,
  });

  const {
    fetchNextPage: fetchNextStorageBoxPage,
    hasNextPage: hasNextStorageBoxPage,
    isLoading: isLoadingStorageBoxes,
    isFetchingNextPage: isLoadingNextStorageBoxPage,
    data: storageBoxesData,
    refetch: refetchStorageBoxes,
  } = getInfiniteStorageBoxes({
    companyId,
    storageboxTotal: storageBoxesCount,
    dateSelected: currentDate,
    branchId,
    pageSize: ROWS_PER_PAGE,
    searches: mountSearchToBackend(["number"], activeSearches.storageBoxSearch),
    filter: activeFilters.storageBoxFilters,
    perspective: viewPerspective,
  });

  const storageBoxes = useMemo(() => {
    if (storageBoxesData && storageBoxesData.pages) {
      const actualPages = [...storageBoxesData.pages];

      const dataPaginated = actualPages
        .map((page) => (page as StorageBox[])?.map((data) => data))
        .flat();

      return dataPaginated;
    }

    return [];
  }, [storageBoxesData]);

  const { mutate: hideCrew } = useHideCrew();

  const collapseAllOrdersTasks = () => {
    setExpandedOrdersTasks([]);
  };

  const expandAllOrdersTasks = () => {
    const tasksIds: number[] = [];

    tasks?.forEach((task) => tasksIds.push(task.id));

    setExpandedOrdersTasks(tasksIds);
  };

  const toggleOrderTaskExpanded = (taskId: number) => {
    if (expandedOrdersTasks?.includes(taskId)) {
      setExpandedOrdersTasks(expandedOrdersTasks?.filter((number) => number !== taskId));
    } else {
      setExpandedOrdersTasks([...expandedOrdersTasks, taskId]);
    }
  };

  const collapseAllTeams = () => {
    setExpandedTeams([]);
  };

  const expandAllTeams = () => {
    const crewsIds: number[] = [];

    crews.forEach((crew) => crewsIds.push(crew.id));

    setExpandedTeams(crewsIds);
  };

  const toggleTeamExpanded = (crewId: number) => {
    if (expandedTeams?.includes(crewId)) {
      setExpandedTeams(expandedTeams?.filter((number) => number !== crewId));
    } else {
      setExpandedTeams([...expandedTeams, crewId]);
    }
  };

  useEffect(() => {
    localStorage.setItem("selectedOption", JSON.stringify(selectedOption));
  }, [selectedOption]);

  useEffect(() => {
    if (crewsFromAPI) {
      setCrews(crewsFromAPI.map((crew) => crew));
      setOldResources((prev) => ({ crews: crewsFromAPI.map((crew) => crew), tasks: tasksFromAPI }));

      const crewsIds: number[] = crewsFromAPI.map((crew) => crew.id);

      setExpandedTeams(crewsIds);
    }
  }, [crewsFromAPI]);

  useEffect(() => {
    if (tasksFromAPI) {
      setTasks(tasksFromAPI);
      setOldResources((prev) => ({ crews: prev?.crews as DespatchCrew[], tasks: tasksFromAPI }));
      const tasksIds: number[] = [];

      tasksFromAPI?.forEach((task) => tasksIds.push(task.id));
      setExpandedOrdersTasks(tasksIds);
    }
  }, [tasksFromAPI]);

  const tasksAlreadyRelatedToCrews = useMemo(() => {
    const allTasks = crews.reduce((prev, curr) => {
      if (curr.isCrew && curr.isAutoCreated && !curr.memberLeader) return prev;

      prev.push(...(curr.tasks || []));

      return prev;
    }, [] as DespatchTask[]);

    const uniqueMap: Map<number, DespatchTask> = new Map();

    allTasks.forEach((task) => {
      uniqueMap.set(task.id, task);
    });

    return Array.from(uniqueMap.values());
  }, [crews]);

  const usersInCrewWithTasks = useMemo(() => {
    const usersCount: Record<number, TaskReference[]> = {};

    crews.forEach((crew) => {
      const leaderId = crew.memberLeader?.id;
      const taskReferences = crew.tasks.map((task) => ({
        id: task.id,
        type: task.type,
        jobReference: task.job.reference,
      }));

      if (leaderId) {
        usersCount[leaderId] ??= [];
        usersCount[leaderId].push(...taskReferences);
      }

      crew.members?.forEach((member) => {
        if (!member.id) return;

        usersCount[member.id] ??= [];
        usersCount[member.id].push(...taskReferences);
      });
    });

    return usersCount;
  }, [crews]);

  // This has to change somehow
  const usersInTasks = useMemo(() => {
    const usersCount: Record<number, TaskReference[]> = {};

    tasks.forEach((task) => {
      const team = task.teams?.find((t) => {
        return differenceInDays(startOfDay(currentDate), startOfDay(new Date(t.createdTo!))) === 0;
      });

      const leaderId = team?.memberLeader?.id;

      if (leaderId) {
        usersCount[leaderId] ??= [];
        usersCount[leaderId].push({
          id: task.id,
          type: task.type,
          jobReference: task.job.reference,
        });
      }

      team?.members?.forEach((member) => {
        usersCount[member.id!] ??= [];
        usersCount[member.id!].push({
          id: task.id,
          type: task.type,
          jobReference: task.job.reference,
        });
      });
    });

    return usersCount;
  }, [tasks]);

  // This has to change somehow
  const equipmentsInTasks = useMemo(() => {
    const equipmentsCount: Record<number, TaskReference[]> = {};

    tasks.forEach((task) => {
      const team = task.teams?.find((t) => {
        return differenceInDays(startOfDay(currentDate), startOfDay(new Date(t.createdTo!))) === 0;
      });

      team?.fleets?.forEach((fleet) => {
        equipmentsCount[fleet.id!] ??= [];
        equipmentsCount[fleet.id!].push({
          id: task.id,
          type: task.type,
          jobReference: task.job.reference,
        });
      });
    });

    return equipmentsCount;
  }, [tasks, equipments]);

  const equipmentsInCrewWithTasks = useMemo(() => {
    const equipmentsCount: Record<number, TaskReference[]> = {};

    crews.forEach((crew) => {
      const taskReferences = crew.tasks.map((task) => ({
        id: task.id,
        type: task.type,
        jobReference: task.job.reference,
      }));

      crew.fleets.forEach((fleet) => {
        equipmentsCount[fleet.id!] ??= [];
        equipmentsCount[fleet.id!].push(...taskReferences);
      });
    });

    return equipmentsCount;
  }, [crews, equipments]);

  const usersInCrewWithCrewName = useMemo(() => {
    const count: Record<number, string[]> = {};

    crews.forEach((_crew) => {
      const { memberLeader, name, members } = _crew;

      if (memberLeader?.id) {
        injectArrayDataIntoRecord(count, memberLeader.id, [String(name)]);
      }

      members?.forEach((member) => {
        if (member?.id) {
          injectArrayDataIntoRecord(count, member.id, [String(name)]);
        }
      });
    });

    return count;
  }, [crews]);

  const equipmentsInCrewWithCrewName = useMemo(() => {
    const count: Record<number, string[]> = {};

    crews.forEach((crew) => {
      crew.fleets.forEach((fleet) => {
        if (fleet.id) {
          injectArrayDataIntoRecord(count, fleet.id, [String(crew.name)]);
        }
      });
    });

    return count;
  }, [crews, equipments]);

  const taskStatusCounter = useMemo(() => {
    if (selectedOption === ResourceViewerOptions.TEAM) {
      return crews.reduce(
        (acc, crew) => {
          const hasTasks = !!crew.tasks.length;

          acc.allTasks += 1;

          if (!hasTasks) return acc;

          const hasUnpublishedTasks = crew.tasks.some((task) =>
            UNPUBLISHED_TASKS.includes(task.status)
          );

          if (hasUnpublishedTasks) {
            acc.unpublishedTasks += 1;
          }

          const hasPublishedTasks = crew.tasks.some((task) =>
            PUBLISHED_TASKS.includes(task.status)
          );

          if (hasPublishedTasks) {
            acc.publishedTasks += 1;
          }

          return acc;
        },
        { allTasks: 0, unpublishedTasks: 0, publishedTasks: 0 }
      );
    }

    return tasks.reduce(
      (acc, task) => {
        acc.allTasks += 1;

        if (UNPUBLISHED_TASKS.includes(task.status)) {
          acc.unpublishedTasks += 1;
        }

        if (PUBLISHED_TASKS.includes(task.status)) {
          acc.publishedTasks += 1;
        }

        return acc;
      },
      { allTasks: 0, unpublishedTasks: 0, publishedTasks: 0 }
    );
  }, [tasks, crews, selectedOption]);

  const findResourceByAttribute = (
    entity: "task" | "crew",
    fieldName: string,
    fieldValue?: string,
    array?: (TeamMember | TeamFleet | TaskStorageBox | DespatchTask)[]
  ) => {
    if (!array || !array.length) return;

    return array.find((item) =>
      matchString({
        data: item,
        key: fieldName,
        value: fieldValue,
        entity,
      })
    );
  };

  const findAndMatchByTaskAttributes = (options: { task: DespatchTask; field: ColumnFilter }) => {
    const { task, field } = options;
    const entity = "task";

    if (!field.name) return;

    const team = task.teams?.find((t) => {
      return differenceInDays(startOfDay(currentDate), startOfDay(new Date(t.createdTo!))) === 0;
    });

    switch (field.name) {
      case DispatchGlobalFilters.MEMBERS:
        return findResourceByAttribute(entity, field.name, field.value, team?.members);

      case DispatchGlobalFilters.EQUIPMENT:
        return findResourceByAttribute(entity, field.name, field.value, team?.fleets);

      case DispatchGlobalFilters.STORAGE_BOXES:
        return findResourceByAttribute(entity, field.name, field.value, task.storageBoxes);

      default:
        return despatchSearchColumns.every((field) => {
          return matchString({
            data: task,
            key: field.name,
            value: field.value,
            entity,
          });
        });
    }
  };

  const findAndMatchByCrewAttributes = (options: { crew: DespatchCrew; field: ColumnFilter }) => {
    const { crew, field } = options;
    const entity = "crew";

    if (!field.name) return;

    switch (field.name) {
      case DispatchGlobalFilters.MEMBERS:
        return findResourceByAttribute(entity, field.name, field.value, crew.members);

      case DispatchGlobalFilters.EQUIPMENT:
        return findResourceByAttribute(entity, field.name, field.value, crew.fleets);

      case DispatchGlobalFilters.STORAGE_BOXES:
        return findResourceByAttribute(entity, field.name, field.value, crew.storageBoxes);

      case DispatchGlobalFilters.ORDER_REFERENCE:
        return findResourceByAttribute(entity, field.name, field.value, crew.tasks);

      default:
        return despatchSearchColumns.every((field) => {
          return matchString({
            data: crew,
            key: field.name,
            value: field.value,
            entity,
          });
        });
    }
  };

  const tasksFiltered = useMemo(() => {
    const response = tasks
      .map((task) => {
        if (checkIsEmptyTask(task) && !showEmptyOrdersTasks) return;

        if (!checkTaskHasAllowedStatus(task)) return;

        if (
          despatchGlobalFilters.tasksTypeNames.length &&
          !despatchGlobalFilters.tasksTypeNames.includes(task.type)
        ) {
          return;
        }

        if (despatchSearchColumns.length) {
          const wasMatched = despatchSearchColumns.every((field) =>
            findAndMatchByTaskAttributes({ task, field })
          );

          if (!wasMatched) return;
        }

        return task;
      })
      .filter(Boolean) as DespatchTask[];

    return response;
  }, [tasks, showEmptyOrdersTasks, despatchGlobalFilters, despatchSearchColumns]);

  const crewsFiltered = useMemo(() => {
    const response = crews
      .map((crew) => {
        if (crew.isHidden || (checkIsEmptyTeam(crew) && !showEmptyTeams)) return;
        if (!checkCrewHasAllowedTaskStatus(crew)) return;
        if (crew.isCrew && crew.isAutoCreated && !crew.memberLeader) return;

        if (
          despatchGlobalFilters.tasksTypeNames.length &&
          !crew.tasks.some((task) => despatchGlobalFilters.tasksTypeNames.includes(task.type))
        ) {
          return;
        }

        if (despatchSearchColumns.length) {
          const wasMatched = despatchSearchColumns.every((field) =>
            findAndMatchByCrewAttributes({ crew, field })
          );

          if (!wasMatched) return;
        }

        return crew;
      })
      .filter(Boolean) as DespatchCrew[];

    return response;
  }, [crews, showEmptyTeams, despatchGlobalFilters, despatchSearchColumns]);

  const tasksAvailable = useMemo(() => {
    const availables = tasks.filter(
      (task) =>
        !tasksAlreadyRelatedToCrews.some((compare) => compare.id === task.id) &&
        UNPUBLISHED_TASKS.includes(task.status)
    );

    if (!taskSearch || !taskSearch.trim()) {
      return availables;
    }

    const textSearch = taskSearch.toLowerCase();

    return availables.filter((task) => {
      const orderCustomerDisplayName = getOrderCustomerDisplayName(task.job).toLowerCase();
      return (
        task.job.reference?.toLowerCase().includes(textSearch) ||
        orderCustomerDisplayName.includes(textSearch) ||
        task.type.toLowerCase().includes(textSearch)
      );
    });
  }, [tasks, tasksAlreadyRelatedToCrews, taskSearch]);

  const generateTeamBranchRelation = (crew: DespatchCrew) => {
    function removeDuplicateObjects(array: Branch[]) {
      const uniqueSet = new Set();
      return array.filter((obj) => {
        const objString = JSON.stringify({ id: obj.id });
        if (!uniqueSet.has(objString)) {
          uniqueSet.add(objString);
          return true;
        }
        return false;
      });
    }

    let branches = crew?.branches;
    const branchList: Branch[] = [];
    crew?.tasks?.forEach((task) => {
      if (task.job) {
        if (task.job.branchFrom) {
          branchList?.push(task.job.branchFrom);
        }

        if (task.job.toBranch && task.job.branchFromId !== task.job.toBranchId) {
          branchList?.push(task.job.toBranch);
        }
      }
    });

    if (branchList?.length) {
      const branchListFromJob = removeDuplicateObjects(branchList);
      branches = removeDuplicateObjects([...(crew?.branches || []), ...(branchListFromJob || [])]);
    }

    return branches;
  };

  const generateCreatedToForCrew = (crewCreatedTo?: Date) => {
    const sourceDate = crewCreatedTo || new Date(currentDate);

    if (crewCreatedTo) return crewCreatedTo;

    const createdToUse = TaskDateUtils.createTaskDateTime(
      {
        dateInput: formatWithUserTimezone(sourceDate, "yyyy-MM-dd"),
      },
      userTimezone.name
    );

    const createdToZonedTime = utcToZonedTime(createdToUse, userTimezone.name);

    return zonedTimeToUtc(createdToZonedTime, userTimezone.name);
  };

  const updateCrewById = (
    id: number,
    updatedCrew: DespatchCrew,
    overrideProperties = false,
    oldTeamToUpdate?: DespatchCrew,
    whatHasChanged?: string[]
  ) => {
    const updatedCrews = [...crews];
    const crewIndex = updatedCrews.findIndex((crew) => crew.id === id || crew.rootTeamId === id);

    const branches = generateTeamBranchRelation(updatedCrew);

    const taskIdsFromTeam = updatedCrew?.tasks?.map((task) => task.id) ?? [];
    const tasksOfTheTeam: DespatchTask[] = [];

    setTasks((prev) =>
      prev?.map((task) => {
        let taskTeam = task;
        if (taskIdsFromTeam.includes(task.id)) {
          const isFirstDay = isTheSameDay(task.startDate, currentDate, userTimezone.name);
          if (isFirstDay) {
            taskTeam = {
              ...task,
              team: updatedCrew,
              leader: updatedCrew.memberLeader?.user,
              leaderId: updatedCrew.memberLeader?.userId,
              memberLeaderId: updatedCrew.memberLeader?.id,
              memberLeader: updatedCrew.memberLeader as Member,
            };
          } else {
            taskTeam = { ...task, team: updatedCrew };
          }

          tasksOfTheTeam.push(taskTeam);
        }

        return taskTeam;
      })
    );

    if (overrideProperties) {
      updatedCrews[crewIndex] = {
        ...(updatedCrews[crewIndex] || {}),
        ...updatedCrew,
      };
    } else {
      if (updatedCrew?.isAutoCreated && !updatedCrew?.tasks?.length) {
        updatedCrews.splice(crewIndex, 1);
      } else {
        updatedCrews[crewIndex] = {
          ...updatedCrew,
          branches,
          tasks: tasksOfTheTeam as DespatchTask[],
        };
      }
    }

    if (oldTeamToUpdate) {
      const oldCrewIndex = updatedCrews.findIndex((crew) => crew.id === oldTeamToUpdate.id);
      if (oldCrewIndex !== -1) {
        if (oldTeamToUpdate?.isAutoCreated && !oldTeamToUpdate?.tasks?.length) {
          updatedCrews.splice(oldCrewIndex, 1);
        } else {
          updatedCrews[oldCrewIndex] = oldTeamToUpdate;
        }

        setCrews(updatedCrews);
      }
    } else {
      setCrews(updatedCrews);
    }

    persistCrewOperation({ ...updatedCrew, branches }, whatHasChanged);
  };

  const moveTaskBetweenCrews = (
    taskId: number,
    fromCrewId: number,
    toCrewId: number,
    task: DespatchTask
  ) => {
    let updatedOldCrew: DespatchCrew | undefined;
    let updatedDestCrew: DespatchCrew | undefined;

    setCrews((prevCrews) => {
      return prevCrews.map((crew) => {
        if (crew.id === fromCrewId && fromCrewId !== toCrewId) {
          // remove task from old crew
          updatedOldCrew = {
            ...crew,
            tasks: crew.tasks.filter((crewTask) => crewTask.id !== taskId),
          };

          return updatedOldCrew;
        }

        if (crew.id === toCrewId) {
          // add task to new crew if not already present
          if (!crew.tasks.some((crewTask) => crewTask.id === taskId)) {
            updatedDestCrew = {
              ...crew,
              tasks: [...crew.tasks, task],
            };

            return updatedDestCrew;
          }
        }

        return crew;
      });
    });

    if (updatedOldCrew) {
      persistCrewOperation(updatedOldCrew);
    }

    if (updatedDestCrew) {
      persistCrewOperation(updatedDestCrew);
    }
  };

  const reverseResources = () => {
    setCrews(oldResources?.crews || []);
    setTasks(oldResources?.tasks || []);
    refetchStorageBoxes();
    refetchStorageBoxCount();

    refetchEquipmentCount();
    refetchEquipmentData();

    refetchMembersCount();
    refetchMembers();
    setChangesMade(null);
  };

  const persistCrewOperation = (
    crew: DespatchCrew,
    whatHasChanged?: string[],
    withChangesMade = true
  ) => {
    const createdTo = generateCreatedToForCrew(crew.createdTo);

    const response = {
      id: crew.id,
      name: crew.name,
      timezone: userTimezone.name,
      companyId: crew.companyId,
      teamKey: crew.teamKey,
      createdTo,
      fleets: crew.fleets?.map((fleet) => ({ id: fleet.id, description: fleet.description })),
      memberLeader: crew.memberLeader
        ? {
            id: crew.memberLeader.id!,
            fullName: crew.memberLeader.fullName,
            userId: crew.memberLeader.userId!,
          }
        : { id: null },
      members: crew.members,
      storageBoxes: crew.storageBoxes?.map((sbox) => ({ id: sbox.id, number: sbox.number })),
      tasks: crew.tasks.map((task) => ({
        id: task.id,
        startDate: task.startDate,
        endDate: task.endDate,
        jobId: task.jobId,
      })),
      isHidden: crew.isHidden,
      whatHasChanged,
      isAutoCreated: crew.isAutoCreated,
      branches: crew.branches?.map((branch) => ({
        id: branch.id,
        name: branch.name,
        valid: branch.valid,
      })),
    };

    if (!withChangesMade) {
      persistCrewChanges({
        data: response,
      });

      return;
    }

    if (!changesMade) {
      setChangesMade([response]);
    } else {
      const findIndex = changesMade.findIndex((changes) => changes.id === response.id);
      if (findIndex === -1) {
        setChangesMade((prev) => [...(prev || []), response]);
      } else {
        setChangesMade((prev) => {
          const newChangesMade = [...(prev || [])];
          newChangesMade[findIndex] = response;
          return newChangesMade;
        });
      }
    }
  };

  const persistCrews = (callback?: () => void) => {
    if (changesMade)
      persistCrewsChanges(changesMade, {
        onSuccess: () => {
          setChangesMade(null);
          setOldResources({ crews, tasks });

          if (callback) callback();
        },
      });
  };

  const taskShouldUpdateLeader = (task: DespatchTask) => {
    if (!isTaskMultiDay(task.period)) return true;

    if (isSameDay(currentDate, new Date(task.startDate))) return true;

    return false;
  };

  const updateTaskById = (id: number, updatedTask: DespatchTask, whatHasChanged?: string[]) => {
    const updatedTasks = [...tasks];

    const taskIndex = updatedTasks.findIndex((task) => task.id === id);
    const taskShouldChangeLeaderAndMemberLeader = taskShouldUpdateLeader(updatedTask);

    if (taskShouldChangeLeaderAndMemberLeader) {
      updatedTask.memberLeader = updatedTask.team?.memberLeader as Member;
      updatedTask.memberLeaderId = updatedTask.team?.memberLeader?.id;
      updatedTask.leaderId = updatedTask.team?.memberLeader?.userId;
      updatedTask.leader = updatedTask.team?.memberLeader?.user;
    }

    updatedTasks[taskIndex] = updatedTask;

    const team = updatedTask.teams?.find((t) => {
      return differenceInDays(startOfDay(currentDate), startOfDay(new Date(t.createdTo!))) === 0;
    });

    const teamIndex = updatedTask.teams?.findIndex((t) => t.id === team?.id)!;

    setCrews((prev) =>
      prev?.map((crew) => {
        if (
          crew.tasks.map((task) => task.id).includes(updatedTask.id) &&
          team &&
          updatedTask.team
        ) {
          return { ...crew, ...team, ...updatedTask.team };
        }

        return crew;
      })
    );

    if (updatedTask?.teams?.length && teamIndex >= 0) {
      const currentTeam = updatedTask.teams[teamIndex];
      updatedTask.teams[teamIndex] = { ...currentTeam, ...updatedTask.team };
    }

    setTasks(updatedTasks);

    // change createdTo to set 00:00
    const createdTo = generateCreatedToForCrew();

    persistTaskChanges({
      data: {
        timezone: userTimezone.name,
        id: updatedTask.id,
        companyId: updatedTask.companyId!,
        teams: [
          {
            id: team?.id,
            memberLeader: updatedTask?.team?.memberLeader
              ? updatedTask?.team?.memberLeader
              : { id: null },
            members: updatedTask.team?.members,
            teamKey: updatedTask.team?.teamKey,
            rootTeamId: team?.rootTeamId!,
            isAutoCreated: updatedTask?.team?.id ? updatedTask?.team?.isAutoCreated : true,
          },
        ],
        fleets: updatedTask.team?.fleets,
        createdTo: createdTo.toISOString(),
        job: {
          id: updatedTask.job.id,
          reference: updatedTask.job.reference,
          toBranchId: updatedTask.job.toBranchId,
          branchFromId: updatedTask.job.branchFromId,
        },
        storageBoxes: updatedTask.storageBoxes,
        whatHasChanged,
      },
    });
  };

  const manageCrewVisible = (crewId: number, isVisible: boolean) => {
    setCrews((compare) =>
      compare.map((crew) => ({
        ...crew,
        isHidden: Number(crew.id) === Number(crewId) ? !isVisible : crew.isHidden,
      }))
    );
  };

  const removeCrewFromBoard = (param: DespatchCrew) => {
    const crewToRemove = crews.find((crew) => crew.id === param.id || crew.rootTeamId === param.id);

    if (!crewToRemove) return;

    const createdTo = generateCreatedToForCrew(crewToRemove.createdTo);

    setCrews((prev) => prev.filter((data) => data.id !== param.id));
    hideCrew({ data: crewToRemove, createdTo });
  };

  const addTeamToBoard = (team: Team) => {
    if (!team || !team.id) return;

    const alreadyAdded = crews.some(
      (crew) => Number(crew.id) === Number(team.id) || team.id === crew.rootTeamId
    );

    if (!alreadyAdded) {
      const crew = {
        id: team.id,
        companyId: team.companyId,
        fleets: team?.fleets,
        members: team?.members,
        name: team.name,
        storageBoxes: [],
        tasks: [],
        memberLeader: team.memberLeader,
        isCrew: team.isCrew,
        isHidden: false,
        teamKey: team.teamKey,
        isAutoCreated: team.isAutoCreated,
      } as DespatchCrew;

      setCrews((state) => [...state, crew]);
      persistCrewOperation(
        {
          ...crew,
          isHidden: false,
        },
        [],
        false
      );

      return;
    }

    manageCrewVisible(team.id, true);
  };

  const isTaskMultiDayPeriod = (period?: number) => {
    if (!period) return false;

    const multiDayPeriodArr = [
      TaskStartDatePeriod.MULTI_DAY,
      TaskStartDatePeriod.FIXED_TIME,
      TaskStartDatePeriod.VARIABLE_TIME,
    ];

    return !!multiDayPeriodArr.includes(period);
  };

  const updateCrewTaskValueByTaskId = (
    taskId: DespatchTask["id"],
    property: keyof DespatchTask,
    value: any
  ) => {
    setCrews((prevCrews) => {
      const crew = prevCrews.find((compare) => compare.tasks.some((task) => task.id === taskId));

      if (crew?.tasks.length) {
        const tasks = crew.tasks.map(
          (task) =>
            ({
              ...task,
              [property]: task.id === taskId ? value : task[property],
            } as DespatchTask)
        );

        const todayTasks = tasks.filter((task) => {
          const taskStartDate = task.startDate
            ? utcToZonedTime(task?.startDate, userTimezone.name)
            : null;
          return (
            (taskStartDate && isSameDay(taskStartDate, currentDate)) ||
            isTaskMultiDayPeriod(task.period)
          );
        });

        return prevCrews.map((stateCrew) => ({
          ...stateCrew,
          tasks: stateCrew.id === crew.id ? todayTasks : stateCrew.tasks,
        }));
      }

      return prevCrews;
    });

    setTasks((prev) => {
      const currentTasks = prev.map((stateTask) => ({
        ...stateTask,
        [property]: stateTask.id === taskId ? value : stateTask[property],
      }));

      const filteredTasks = currentTasks.filter((task) => {
        const taskStartDate = task.startDate
          ? utcToZonedTime(task?.startDate, userTimezone.name)
          : null;
        return (
          (taskStartDate && isSameDay(taskStartDate, currentDate)) ||
          isTaskMultiDayPeriod(task.period)
        );
      });
      return filteredTasks;
    });
  };

  const addNewCrew = (crew: DespatchCrew) => {
    const foundByIdOrName = crews.some(
      (compare) => compare.id === crew.id || crew.id === compare.rootTeamId
    );

    if (!foundByIdOrName && crew.id && !crew.rootTeamId) {
      setCrews((prev) => [crew, ...prev]);
    }
  };

  const addNewStorageBoxToCrew = (crew: DespatchCrew, storageBox: StorageBox) => {
    if (crew && crew.id && storageBox && storageBox.id) {
      const actualStoraBoxes = crew.storageBoxes || [];

      actualStoraBoxes.push(storageBox);
      updateCrewById(
        crew.id,
        { ...crew, storageBoxes: actualStoraBoxes } as DespatchCrew,
        true,
        undefined,
        ["storageBoxes"]
      );
    }
  };

  const addTaskCreatedToBoard = (task: Task) => {
    if (!task?.id) return;
    refetchTasks();
  };

  const addTaskComment = (taskId: DespatchTask["id"], comment: DespatchTaskComment) => {
    const despatchTaskComment = {
      id: comment.id,
      createdAt: comment.createdAt,
      message: comment.message,
      userId: comment.userId,
      user: {
        fullName: currentUser?.fullName!,
        id: currentUser?.id!,
        picture: currentUser?.picture!,
      },
    };

    const crew = crews.find((compare) => compare.tasks.some((task) => task.id === taskId));

    if (crew) {
      const tasks = crew.tasks.map((task) => {
        if (task.id !== taskId) return task;

        const comments = [...(task?.comments || [])];

        comments.push(despatchTaskComment);

        return {
          ...task,
          comments,
        };
      });

      setCrews((prev) =>
        prev.map((stateCrew) => ({
          ...stateCrew,
          tasks: stateCrew.id === crew.id ? tasks : stateCrew.tasks,
        }))
      );
    }

    setTasks((prev) =>
      prev.map((stateTask) => {
        if (stateTask.id !== taskId) return stateTask;

        const comments = [...(stateTask?.comments || [])];

        comments.push(despatchTaskComment);

        return {
          ...stateTask,
          comments,
        };
      })
    );
  };

  const deleteTaskComment = (taskId: DespatchTask["id"], commentId: DespatchTaskComment["id"]) => {
    const crew = crews.find((compare) => compare.tasks.some((task) => task.id === taskId));

    if (crew) {
      const tasks = (crew.tasks || []).map((task) => {
        if (task.id !== taskId) return task;

        const comments = task?.comments || [];

        return {
          ...task,
          comments: comments.filter((comment) => comment.id !== commentId),
        };
      });

      setCrews((prev) =>
        prev.map((stateCrew) => ({
          ...stateCrew,
          tasks: stateCrew.id === crew.id ? tasks : stateCrew.tasks,
        }))
      );
    }

    setTasks((prev) =>
      prev.map((stateTask) => {
        if (stateTask.id !== taskId) return stateTask;

        const comments = stateTask?.comments || [];

        return {
          ...stateTask,
          comments: comments.filter((comment) => comment.id !== commentId),
        };
      })
    );
  };

  const deletePlaceholderTask = (taskId: DespatchTask["id"]) => {
    setTasks((prev) => prev.filter((task) => task.id !== taskId));
    setCrews((prev) =>
      prev.map((stateCrew) => ({
        ...stateCrew,
        tasks: stateCrew.tasks.filter((task) => task.id !== taskId),
      }))
    );
  };

  const cancelTaskRef = useRef<DespatchCancelTaskModalHandles>(null);
  const deletePlaceholderTaskRef = useRef<DespatchDeletePlaceholderTaskModalHandles>(null);
  const changeTimeRef = useRef<DespatchChangeTimeModalHandles>(null);
  const storageBoxSingleUseRef = useRef<StorageBoxSingleUseModalHandles>(null);
  const createTaskRef = useRef<DespatchCreateTaskModalHandles>(null);
  const taskCommentRef = useRef<DespatchTaskCommentsModalHandles>(null);
  const despatchTimeControlRef = useRef<TimeControlAndExpensePropsHandles>(null);
  const seeTasksModalRef = useRef<DiarySeeTasksHandles>(null);
  const createCrewModalRef = useRef<CreateCrewModalHandles>(null);
  const createPlaceholderTaskRef = useRef<DespatchCreateTaskModalHandles>(null);

  return (
    <OperationDespatchContext.Provider
      value={{
        companyId,
        branchId,
        despatchSearchColumns,
        setDespatchSearchColumns,
        despatchFilters,
        setDespatchFilters,
        changesMade,
        setChangesMade,
        reverseResources,
        persistCrews,

        seeTasksModalRef,
        despatchTimeControlRef,
        cancelTaskRef,
        deletePlaceholderTaskRef,
        changeTimeRef,
        storageBoxSingleUseRef,
        createTaskRef,
        taskCommentRef,
        createCrewModalRef,
        createPlaceholderTaskRef,

        fullscreenContainerRef,
        toogleFullscreen,
        isFullscreen,
        isSearchMode,
        setIsSearchMode,

        despatchGlobalFilters,
        setDespatchGlobalFilters,

        selectedOption,
        setSelectedOption,
        draggedItemHeight,
        setDraggedItemHeight,

        viewMode,
        setViewMode,

        searchTerm,
        setSearchTerm,
        removeCrewFromBoard,
        addTeamToBoard,
        searchValue,
        updateCrewById,
        moveTaskBetweenCrews,
        updateTaskById,
        isLoadingPersisTaskChanges,
        isLoadingPersistCrewsChanges,

        currentDate,
        setCurrentDate,
        currentDraggable,
        setCurrentDraggable,

        parentOfSource,
        setParentOfSource,

        updateCrewTaskValueByTaskId,
        addNewCrew,
        addNewStorageBoxToCrew,
        addTaskCreatedToBoard,

        usersInTasks,
        usersInCrewWithTasks,
        equipmentsInCrewWithTasks,
        equipmentsInTasks,

        usersInCrewWithCrewName,
        equipmentsInCrewWithCrewName,

        crews,
        crewsFiltered,
        setCrews,
        isLoadingCrews,
        isFetchingCrews,
        isFetchedCrews,
        isRefetchingCrews,

        refetchTasks,
        taskStatusCounter,
        tasks,
        tasksFiltered,
        tasksAvailable,
        taskSearch,
        taskSetSearch,
        deletePlaceholderTask,
        isFetchingTasks,
        isFetchedTasks,
        isRefetchingTasks,

        addTaskComment,
        deleteTaskComment,

        members,
        membersCount,
        refetchMembers,
        isLoadingMembers,

        teams,
        teamsCount,
        isLoadingTeams,

        fetchNextMemberPage,
        isLoadingNextMemberPage,
        hasNextMemberPage: !!hasNextMemberPage,

        equipments,
        equipmentsCount,
        isLoadingEquipments,
        fetchNextEquipmentPage,
        isLoadingNextEquipmentPage,
        hasNextEquipmentPage: !!hasNextEquipmentPage,

        storageBoxes,
        storageBoxesCount,
        isLoadingStorageBoxes,
        fetchNextStorageBoxPage,
        isLoadingNextStorageBoxPage,
        hasNextStorageBoxPage: !!hasNextStorageBoxPage,

        expandedOrdersTasks,
        collapseAllOrdersTasks,
        expandAllOrdersTasks,
        toggleOrderTaskExpanded,

        expandedTeams,
        collapseAllTeams,
        expandAllTeams,
        toggleTeamExpanded,

        showUnavailableItems,
        setShowUnavailableItems,
        showEmptyOrdersTasks,
        setShowEmptyOrdersTasks,
        showEmptyTeams,
        setShowEmptyTeams,

        activeFilters,
        setActiveFilters,

        viewPerspective,
        setViewPerspective,

        activeSearches,
        setActiveSearches,

        showLeftSideView,
        showRightSideView,
        setShowLeftSideView,
        setShowRightSideView,
        totalItemsPerColumn,
      }}
    >
      {children}
    </OperationDespatchContext.Provider>
  );
};

const useOperationDespatchContext = () => {
  const context = useContext(OperationDespatchContext);

  return context;
};

export { OperationDespatchProvider, useOperationDespatchContext };
